import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Pagination,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import PersonRemoveRoundedIcon from "@mui/icons-material/PersonRemoveRounded";
import { IUsuario } from "../../../Hooks/UsuarioHook";
import { useUsuarioStorage } from "../../../Storage/UsuariosStorage";

// ──────────────────────────────────────────────────────────────────────────────
// Gerencia quem tem acesso a um sistema. Aberto pelo botão que só aparece para
// administradores no card do sistema.
//
// Duas abas:
//   "Com acesso" — quem já usa o sistema (vínculo em SISTEMA_USUARIO) → remover
//   "Sem acesso" — demais usuários da entidade                        → adicionar
//
// A situação do usuário (ativo/inativo) NÃO separa as abas: vira um chip na
// linha, porque aqui o que se administra é o acesso, não o cadastro.
//
// As duas abas são paginadas — há sistema com 470 usuários e entidade com 2500,
// e montar tudo de uma vez travava o modal. "Com acesso" pagina no cliente (a
// lista já vem inteira do endpoint); "Sem acesso" pagina no servidor.
// ──────────────────────────────────────────────────────────────────────────────

interface Props {
  sistemaId: number;
  titulo: string;
  onFechar: () => void;
}

/** Linhas por página. Cabe na altura do modal sem precisar rolar. */
const POR_PAGINA = 6;

function mascaraCpfCnpj(v?: string | null): string {
  const d = String(v ?? "").replace(/\D/g, "");
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  if (d.length === 14)
    return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  return String(v ?? "");
}

function iniciais(nome?: string | null): string {
  const partes = String(nome ?? "").trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "?";
  return (partes[0][0] + (partes.length > 1 ? partes[partes.length - 1][0] : "")).toUpperCase();
}

/** Ordena por nome */
function porNome(a: IUsuario, b: IUsuario) {
  return String(a.usuario?.nome ?? "").localeCompare(String(b.usuario?.nome ?? ""));
}

/** Filtro client-side (nome, e-mail e documento) usado na aba "Com acesso" */
function casaComBusca(item: IUsuario, termo: string): boolean {
  if (!termo) return true;
  const u = item.usuario;
  const alvo = `${u?.nome ?? ""} ${u?.email ?? ""}`.toLowerCase();
  if (alvo.includes(termo)) return true;
  const digitos = termo.replace(/\D/g, "");
  if (!digitos) return false;
  return `${u?.cpf ?? ""}${u?.cnpj ?? ""}`.includes(digitos);
}

// ── Linha de um usuário ───────────────────────────────────────────────────────

interface LinhaProps {
  item: IUsuario;
  /** "remover" na aba com acesso, "adicionar" na aba sem acesso */
  acao: "adicionar" | "remover";
  ocupado: boolean;
  /** trava as outras linhas enquanto uma requisição está em andamento */
  desabilitado: boolean;
  confirmando: boolean;
  onPedirConfirmacao: () => void;
  onCancelar: () => void;
  onConfirmar: () => void;
}

const LinhaUsuario: React.FC<LinhaProps> = ({
  item,
  acao,
  ocupado,
  desabilitado,
  confirmando,
  onPedirConfirmacao,
  onCancelar,
  onConfirmar,
}) => {
  const u = item.usuario;
  const documento = mascaraCpfCnpj(u?.cpf || u?.cnpj);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.25,
        borderRadius: 2,
        border: 1,
        borderColor: confirmando ? "error.main" : "divider",
        bgcolor: "background.paper",
        transition: "border-color .18s ease",
      }}
    >
      <Avatar
        src={u?.foto || undefined}
        alt={u?.nome}
        sx={{ width: 40, height: 40, bgcolor: "primary.main", flexShrink: 0 }}
      >
        {iniciais(u?.nome)}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography fontWeight={700} noWrap>
          {u?.nome || "—"}
        </Typography>

        {confirmando ? (
          <Typography variant="body2" color="error.main" noWrap>
            Remover o acesso deste usuário?
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" noWrap>
            {[documento, u?.email].filter(Boolean).join(" · ") || "—"}
          </Typography>
        )}
      </Box>

      {/* A confirmação acontece na própria linha — Dialog dentro de Dialog empilha
          camadas e faz o modal inteiro piscar a cada remoção. */}
      {confirmando ? (
        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Button size="small" onClick={onCancelar} disabled={ocupado}>
            Cancelar
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={onConfirmar}
            disabled={ocupado}
            startIcon={
              ocupado ? <CircularProgress size={14} color="inherit" /> : undefined
            }
          >
            Remover
          </Button>
        </Stack>
      ) : (
        <>
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}
          >
            {/* Boolean() é obrigatório: administrador/servidor vêm do MySQL
                como tinyint (0/1) e `0 && <Chip/>` renderiza o literal 0 */}
            {Boolean(u?.administrador) && (
              <Chip label="Admin" size="small" color="primary" />
            )}
            {Boolean(u?.servidor) && (
              <Chip label="Servidor" size="small" variant="outlined" />
            )}
            {Boolean(u?.inativo) && (
              <Chip label="Inativo" size="small" color="error" />
            )}
          </Stack>

          <Tooltip
            title={acao === "remover" ? "Remover acesso" : "Liberar acesso"}
            arrow
          >
            <span>
              <IconButton
                size="small"
                color={acao === "remover" ? "error" : "primary"}
                disabled={desabilitado}
                onClick={onPedirConfirmacao}
                aria-label={`${
                  acao === "remover" ? "Remover acesso de" : "Liberar acesso para"
                } ${u?.nome ?? "usuário"}`}
                sx={{ flexShrink: 0 }}
              >
                {ocupado ? (
                  <CircularProgress size={18} color="inherit" />
                ) : acao === "remover" ? (
                  <PersonRemoveRoundedIcon fontSize="small" />
                ) : (
                  <PersonAddAlt1RoundedIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </>
      )}
    </Box>
  );
};

// ── Modal ─────────────────────────────────────────────────────────────────────

export const UsuariosDoSistemaModal: React.FC<Props> = ({
  sistemaId,
  titulo,
  onFechar,
}) => {
  const {
    listarUsuariosPorSistema,
    buscarUsuariosDaEntidade,
    vincularUsuarioAoSistema,
    desvincularUsuarioDoSistema,
  } = useUsuarioStorage();

  const [aba, setAba] = useState<"com" | "sem">("com");
  const [busca, setBusca] = useState("");

  // aba "Com acesso" — vem inteira do endpoint, filtrada e paginada no cliente
  const [comAcesso, setComAcesso] = useState<IUsuario[]>([]);
  const [carregandoCom, setCarregandoCom] = useState(true);
  const [erroCom, setErroCom] = useState<string | null>(null);
  const [paginaCom, setPaginaCom] = useState(0);

  // aba "Sem acesso" — busca e paginação no servidor, com debounce
  const [candidatos, setCandidatos] = useState<IUsuario[]>([]);
  const [totalSem, setTotalSem] = useState(0);
  const [carregandoSem, setCarregandoSem] = useState(false);
  const [erroSem, setErroSem] = useState<string | null>(null);
  const [paginaSem, setPaginaSem] = useState(0);

  const [ocupadoId, setOcupadoId] = useState<number | null>(null);
  const [confirmarId, setConfirmarId] = useState<number | null>(null);
  const [erroAcao, setErroAcao] = useState<string | null>(null);
  // incrementado a cada vínculo criado/removido: refaz a busca de candidatos
  const [versao, setVersao] = useState(0);

  // ── carga da aba "Com acesso" ───────────────────────────────────────────────
  useEffect(() => {
    let ativo = true;
    setCarregandoCom(true);
    setErroCom(null);
    listarUsuariosPorSistema(sistemaId)
      .then((lista) => {
        if (!ativo) return;
        setComAcesso(lista.slice().sort(porNome));
      })
      .catch((e: any) => {
        if (!ativo) return;
        setErroCom(
          e?.response?.data?.mensagem ??
            e?.message ??
            "Não foi possível carregar os usuários deste sistema."
        );
      })
      .finally(() => {
        if (ativo) setCarregandoCom(false);
      });
    return () => {
      ativo = false;
    };
  }, [sistemaId]);

  const idsComAcesso = useMemo(
    () => new Set(comAcesso.map((i) => Number(i.usuario?.id))),
    [comAcesso]
  );

  // ── busca da aba "Sem acesso" (servidor, com debounce) ──────────────────────
  useEffect(() => {
    if (aba !== "sem") return;
    let ativo = true;
    setCarregandoSem(true);
    setErroSem(null);
    const t = setTimeout(() => {
      buscarUsuariosDaEntidade({
        search: busca.trim(),
        page: paginaSem,
        pageSize: POR_PAGINA,
        semAcesso: sistemaId,
      })
        .then(({ lista, total }) => {
          if (!ativo) return;
          setCandidatos(lista);
          setTotalSem(total);
          // a página esvaziou (liberou o último item dela): recua uma
          if (lista.length === 0 && paginaSem > 0) {
            setPaginaSem((p) => Math.max(0, p - 1));
          }
        })
        .catch((e: any) => {
          if (!ativo) return;
          setErroSem(
            e?.response?.data?.mensagem ??
              e?.message ??
              "Não foi possível buscar os usuários da entidade."
          );
        })
        .finally(() => {
          if (ativo) setCarregandoSem(false);
        });
    }, 350);
    return () => {
      ativo = false;
      clearTimeout(t);
    };
  }, [aba, busca, sistemaId, versao, paginaSem]);

  // ── aba "Com acesso": filtro + paginação no cliente ─────────────────────────
  const termo = busca.trim().toLowerCase();

  const listaCom = useMemo(
    () => comAcesso.filter((i) => casaComBusca(i, termo)),
    [comAcesso, termo]
  );

  const totalPaginasCom = Math.max(1, Math.ceil(listaCom.length / POR_PAGINA));
  // clamp: remover o último item de uma página deixaria a página fora do intervalo
  const paginaComSegura = Math.min(paginaCom, totalPaginasCom - 1);
  const visiveisCom = listaCom.slice(
    paginaComSegura * POR_PAGINA,
    paginaComSegura * POR_PAGINA + POR_PAGINA
  );

  // Rede de segurança: se o backend ainda não conhecer o filtro `semAcesso`, quem
  // já tem vínculo viria na lista de candidatos. O corte aqui evita mostrar um
  // botão de liberar que responderia "Já existe uma ligação!".
  const visiveisSem = useMemo(
    () => candidatos.filter((i) => !idsComAcesso.has(Number(i.usuario?.id))),
    [candidatos, idsComAcesso]
  );
  const totalPaginasSem = Math.max(1, Math.ceil(totalSem / POR_PAGINA));

  // ── ações ───────────────────────────────────────────────────────────────────
  async function adicionar(item: IUsuario) {
    const id = Number(item.usuario?.id);
    setOcupadoId(id);
    setErroAcao(null);
    try {
      await vincularUsuarioAoSistema(id, sistemaId);
      // entra na lista da outra aba sem precisar recarregá-la
      setComAcesso((prev) => prev.concat(item).sort(porNome));
      setVersao((v) => v + 1);
    } catch (e: any) {
      setErroAcao(
        e?.response?.data?.mensagem ??
          e?.message ??
          "Não foi possível liberar o acesso."
      );
    } finally {
      setOcupadoId(null);
    }
  }

  async function remover(item: IUsuario) {
    const id = Number(item.usuario?.id);
    setOcupadoId(id);
    setErroAcao(null);
    try {
      await desvincularUsuarioDoSistema(id, sistemaId);
      setComAcesso((prev) => prev.filter((c) => Number(c.usuario?.id) !== id));
      // volta a ser candidato: força a rebusca da aba "Sem acesso"
      setVersao((v) => v + 1);
      setConfirmarId(null);
    } catch (e: any) {
      setErroAcao(
        e?.response?.data?.mensagem ??
          e?.message ??
          "Não foi possível remover o acesso."
      );
    } finally {
      setOcupadoId(null);
    }
  }

  function trocarAba(_: unknown, nova: "com" | "sem") {
    setAba(nova);
    setConfirmarId(null);
    setErroAcao(null);
  }

  function mudarBusca(valor: string) {
    setBusca(valor);
    setConfirmarId(null);
    // busca nova recomeça da primeira página nas duas abas
    setPaginaCom(0);
    setPaginaSem(0);
  }

  const carregando = aba === "com" ? carregandoCom : carregandoSem;
  const erroLista = aba === "com" ? erroCom : erroSem;
  const visiveis = aba === "com" ? visiveisCom : visiveisSem;
  const totalPaginas = aba === "com" ? totalPaginasCom : totalPaginasSem;
  const paginaAtual = aba === "com" ? paginaComSegura : paginaSem;

  return (
    <Dialog
      open
      onClose={onFechar}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      {/* Cabeçalho */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 2,
          background: "linear-gradient(135deg, #2563eb 0%, #4f8ef7 100%)",
          color: "#fff",
        }}
      >
        <GroupsRoundedIcon />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={800} sx={{ lineHeight: 1.2 }}>
            Usuários do sistema
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }} noWrap>
            {titulo}
          </Typography>
        </Box>
        <IconButton onClick={onFechar} size="small" sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Tabs
        value={aba}
        onChange={trocarAba}
        variant="fullWidth"
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Tab
          value="com"
          label={carregandoCom ? "Com acesso" : `Com acesso (${comAcesso.length})`}
          sx={{ fontWeight: 700, textTransform: "none" }}
        />
        <Tab
          value="sem"
          label="Sem acesso"
          sx={{ fontWeight: 700, textTransform: "none" }}
        />
      </Tabs>

      <DialogContent sx={{ bgcolor: "background.default", p: 2 }}>
        <Stack spacing={1.5}>
          <TextField
            size="small"
            fullWidth
            autoComplete="off"
            placeholder={
              aba === "com"
                ? "Filtrar por nome, CPF/CNPJ ou e-mail..."
                : "Pesquisar por nome ou CPF/CNPJ para liberar..."
            }
            value={busca}
            onChange={(e) => mudarBusca(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 50 } }}
          />

          {erroAcao && (
            <Alert severity="error" onClose={() => setErroAcao(null)}>
              {erroAcao}
            </Alert>
          )}

          {/* Altura fixa: com a paginação a lista tem sempre no máximo POR_PAGINA
              linhas, então o modal não muda de tamanho ao trocar de aba/página */}
          <Box sx={{ minHeight: 300, maxHeight: 400, overflowY: "auto", pr: 0.5 }}>
            {carregando ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
              </Box>
            ) : erroLista ? (
              <Alert severity="error">{erroLista}</Alert>
            ) : visiveis.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
                <Typography color="text.secondary">
                  {aba === "com"
                    ? termo
                      ? "Nenhum usuário encontrado com esse filtro."
                      : "Nenhum usuário tem acesso a este sistema."
                    : termo
                    ? "Nenhum usuário sem acesso encontrado."
                    : "Todos os usuários da entidade já têm acesso."}
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1}>
                {visiveis.map((item) => {
                  const id = Number(item.usuario?.id);
                  return (
                    <LinhaUsuario
                      key={id}
                      item={item}
                      acao={aba === "com" ? "remover" : "adicionar"}
                      ocupado={ocupadoId === id}
                      desabilitado={ocupadoId !== null && ocupadoId !== id}
                      confirmando={confirmarId === id}
                      onPedirConfirmacao={() => {
                        // liberar acesso é reversível: vai direto.
                        // remover tira alguém de um sistema em uso: confirma.
                        if (aba === "com") setConfirmarId(id);
                        else adicionar(item);
                      }}
                      onCancelar={() => setConfirmarId(null)}
                      onConfirmar={() => remover(item)}
                    />
                  );
                })}
              </Stack>
            )}
          </Box>

          {/* Contagem + páginas — somem enquanto carrega para não exibir dado velho */}
          {!carregando && !erroLista && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              sx={{ flexWrap: "wrap", gap: 1 }}
            >
              <Typography variant="caption" color="text.secondary">
                {aba === "com"
                  ? termo
                    ? `${listaCom.length} de ${comAcesso.length} com acesso`
                    : `${comAcesso.length} ${
                        comAcesso.length === 1 ? "usuário" : "usuários"
                      } com acesso`
                  : `${totalSem} ${
                      totalSem === 1 ? "usuário" : "usuários"
                    } sem acesso`}
              </Typography>

              {totalPaginas > 1 && (
                <Pagination
                  size="small"
                  color="primary"
                  // entidade grande chega a centenas de páginas: sem os vizinhos
                  // o controle não estoura a largura do modal
                  siblingCount={0}
                  boundaryCount={1}
                  count={totalPaginas}
                  page={paginaAtual + 1}
                  onChange={(_, p) => {
                    setConfirmarId(null);
                    if (aba === "com") setPaginaCom(p - 1);
                    else setPaginaSem(p - 1);
                  }}
                />
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
